import React from 'react';
import type { Chat, BaseComponentProps } from '@/types';

interface ChatListItemProps extends BaseComponentProps {
  chat: Chat;
  isActive: boolean;
  onClick: (chat: Chat) => void;
}

const ChatListItem: React.FC<ChatListItemProps> = ({ 
  chat, 
  isActive, 
  onClick,
  className = '' 
}) => {
  const handleClick = () => {
    onClick(chat);
  };

  const formatLastMessageTime = (timestamp: Date): string => {
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'now';
    } else if (diffInHours < 24) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return messageDate.toLocaleDateString([], { weekday: 'short' });
    } else {
      return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const truncateMessage = (content: string, maxLength = 50): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div
      onClick={handleClick}
      className={`
        p-4 cursor-pointer transition-colors duration-150 border-b border-gray-100
        hover:bg-gray-50 active:bg-gray-100
        ${isActive ? 'bg-primary-50 border-primary-200' : ''}
        ${className}
      `}
    >
      <div className="flex items-start space-x-3">
        {/* Chat Avatar */}
        <div className="flex-shrink-0">
          {chat.type === 'direct' && chat.participants.length > 1 ? (
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-medium text-lg">
                {chat.participants
                  .find(p => p.id !== 'current-user-id')?.username.charAt(0)
                  .toUpperCase() || '?'}
              </span>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-600 font-medium text-lg">
                {chat.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Chat Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {chat.name}
            </h3>
            {chat.lastMessage && (
              <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                {formatLastMessageTime(chat.lastMessage.timestamp)}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 truncate">
              {chat.lastMessage ? (
                truncateMessage(chat.lastMessage.content)
              ) : (
                <span className="text-gray-400 italic">No messages yet</span>
              )}
            </p>
            
            {chat.unreadCount > 0 && (
              <div className="flex-shrink-0 ml-2">
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white bg-primary-600 rounded-full min-w-[20px]">
                  {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ChatListProps extends BaseComponentProps {
  chats: Chat[];
  activeChat?: Chat;
  isLoading?: boolean;
  onChatSelect: (chat: Chat) => void;
  onRefresh?: () => void;
}

const ChatList: React.FC<ChatListProps> = ({ 
  chats, 
  activeChat, 
  isLoading = false,
  onChatSelect,
  onRefresh,
  className = '' 
}) => {
  if (isLoading && chats.length === 0) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-gray-500 text-sm">Loading chats...</p>
        </div>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className={`flex items-center justify-center py-8 px-4 ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <p className="text-gray-500 text-sm mb-2">No chats yet</p>
          <p className="text-gray-400 text-xs">Start a conversation to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white sticky top-0">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Chats</h2>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="btn-ghost p-2 rounded-md disabled:opacity-50"
              title="Refresh chats"
            >
              <svg 
                className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Chat List */}
      <div className="overflow-y-auto">
        {chats.map((chat) => (
          <ChatListItem
            key={chat.id}
            chat={chat}
            isActive={activeChat?.id === chat.id}
            onClick={onChatSelect}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatList;